import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

import { DeliveryService, DeliveryRoute, DriverAccount } from 'src/app/services/delivery.service';
import { AuthService } from 'src/app/services/auth.service';
import { NotificationService } from 'src/app/shared/notification.service';
import { ConfigService } from 'src/app/shared/config';

// "How is today going" — the one screen an operator keeps open.
//
// Before this existed the answer lived in three places: Routes (did anybody
// generate a run?), Dispatch (how far are they?) and Issues (what is broken?).
// This screen puts the three on one page and keeps the actions where the
// question is asked: generate the missing run right here, open the detail in
// Dispatch, jump to the issue list. Nothing is deleted — Dispatch is still the
// stop-by-stop board, this is the day at a glance.
//
// NOTE on *ngFor: every list bound in the template is a plain FIELD recomputed
// when data arrives, never a getter. Getters that mint arrays or objects have
// frozen this dashboard four times.

interface RunCard {
    id: string;
    routeId: string;
    routeName: string;
    driverUid: string | null;
    driverName: string;
    status: string;
    statusLabel: string;
    statusClass: string;
    stopCount: number;
    completedStopCount: number;
    skippedStopCount: number;
    progressPct: number;
    /** "At Eramet (stop 3)" / "Driving — last done Eramet" — from the run stamps. */
    whereLabel: string;
    lastActivity: string;
    pickedUpAt: string;
    completedAt: string;
    expectedLabel: string;
    issueCount: number;
    /** "covering for Ola" — somebody took this route over for the day. */
    coveringLabel: string;
    /** Sorting weight: trouble first, then the people still out, then done. */
    weight: number;
}

interface UnscheduledRoute {
    id: string;
    name: string;
    stopCount: number;
    defaultDriverUid: string | null;
    defaultDriverName: string;
    generating: boolean;
}

interface IssueCard {
    id: string;
    systemName: string;
    categoryLabel: string;
    note: string;
    when: string;
}

interface LastSeen {
    label: string;
    mapsUrl: string;
    stale: boolean;
}

const CATEGORY_LABELS: { [key: string]: string } = {
    machine: 'Machine down',
    payment: 'Payment terminal',
    temperature: 'Temperature',
    damage: 'Damage',
    access: 'Access',
    other: 'Other'
};

@Component({
    selector: 'app-delivery-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.css']
})
export class DeliveryOverviewComponent implements OnInit, OnDestroy {

    date = moment().format('YYYY-MM-DD');
    loading = true;
    canEdit = false;

    runs: RunCard[] = [];
    unscheduled: UnscheduledRoute[] = [];
    issues: IssueCard[] = [];
    openIssueCount = 0;

    // Headline numbers, recomputed whenever runs arrive.
    runCount = 0;
    doneRunCount = 0;
    onRoadCount = 0;
    notStartedCount = 0;
    stopsDone = 0;
    stopsTotal = 0;
    stopsSkipped = 0;
    unassignedCount = 0;

    lastSeen: { [driverUid: string]: LastSeen } = {};
    activeDrivers: DriverAccount[] = [];
    /** Driver chosen per route in the "no run yet" list; keyed by route id. */
    genDriver: { [routeId: string]: string } = {};

    private routes: DeliveryRoute[] = [];
    private runsLoaded = false;
    private routesLoaded = false;

    private runsSub: Subscription | null = null;
    private subs: Subscription[] = [];
    private locationSubs = new Map<string, Subscription>();

    constructor(
        private svc: DeliveryService,
        private auth: AuthService,
        private notifier: NotificationService,
        private config: ConfigService,
        private router: Router
    ) { }

    ngOnInit() {
        this.subs.push(this.auth.user$.subscribe(user => {
            this.canEdit = user ? this.auth.canEdit(user) : false;
        }));

        this.subs.push(this.svc.routes$().subscribe(routes => {
            this.routes = (routes || []).filter(r => r.active !== false);
            this.routesLoaded = true;
            this.recomputeUnscheduled();
        }, () => { this.routesLoaded = true; }));

        this.subs.push(this.svc.issues$('open').subscribe(list => {
            this.openIssueCount = (list || []).length;
            this.issues = (list || []).slice(0, 6).map(i => ({
                id: i.id,
                systemName: i.systemName || i.systemId || '—',
                categoryLabel: CATEGORY_LABELS[i.category] || i.category || 'Other',
                note: i.note || '',
                when: i.createdAt ? moment.unix(i.createdAt).format('DD.MM HH:mm') : ''
            }));
        }));

        this.loadDrivers();
        this.subscribeRuns();
    }

    ngOnDestroy() {
        this.runsSub?.unsubscribe();
        this.subs.forEach(s => s.unsubscribe());
        this.locationSubs.forEach(s => s.unsubscribe());
    }

    private async loadDrivers() {
        try {
            const drivers = await this.svc.listDrivers(this.config.getCompanyId());
            this.activeDrivers = (drivers || []).filter(d => d.status);
        } catch {
            // A missing driver list must not blank the board; a run can be
            // generated unassigned and handed over later.
            this.activeDrivers = [];
        }
    }

    // ---- date ---------------------------------------------------------------

    onDateChange() { this.subscribeRuns(); }

    shiftDate(days: number) {
        this.date = moment(this.date, 'YYYY-MM-DD').add(days, 'day').format('YYYY-MM-DD');
        this.subscribeRuns();
    }

    goToday() {
        this.date = moment().format('YYYY-MM-DD');
        this.subscribeRuns();
    }

    isToday(): boolean { return this.date === moment().format('YYYY-MM-DD'); }

    dateLabel(): string {
        return moment(this.date, 'YYYY-MM-DD').format('dddd D MMMM');
    }

    // ---- data ---------------------------------------------------------------

    private subscribeRuns() {
        this.runsSub?.unsubscribe();
        this.loading = true;
        this.runsLoaded = false;
        this.runsSub = this.svc.runsForDate$(this.date).subscribe(list => {
            this.runs = (list || [])
                .map(r => this.toCard(r))
                .sort((a, b) => a.weight - b.weight || a.routeName.localeCompare(b.routeName));
            this.summarise();
            this.watchDriverLocations();
            this.runsLoaded = true;
            this.loading = false;
            this.recomputeUnscheduled();
        }, () => { this.loading = false; this.runsLoaded = true; });
    }

    private summarise() {
        this.runCount = this.runs.length;
        this.doneRunCount = this.runs.filter(r => r.status === 'completed').length;
        this.onRoadCount = this.runs.filter(r => r.status === 'in_progress' || r.status === 'picked_up').length;
        this.notStartedCount = this.runs.filter(r => r.status === 'assigned').length;
        this.unassignedCount = this.runs.filter(r => !r.driverUid).length;
        this.stopsDone = this.runs.reduce((s, r) => s + r.completedStopCount, 0);
        this.stopsTotal = this.runs.reduce((s, r) => s + r.stopCount, 0);
        this.stopsSkipped = this.runs.reduce((s, r) => s + r.skippedStopCount, 0);
    }

    // Routes with work but no run for the selected date — the single most
    // common "why is nobody driving this" cause.
    private recomputeUnscheduled() {
        if (!this.routesLoaded || !this.runsLoaded) return;
        const covered = new Set(this.runs.map(r => r.routeId));
        this.unscheduled = this.routes
            .filter(r => r.id && !covered.has(r.id) && (r.stops || []).length > 0)
            .map(r => {
                if (this.genDriver[r.id!] === undefined) {
                    this.genDriver[r.id!] = r.defaultDriverUid || '';
                }
                return {
                    id: r.id!,
                    name: r.name || r.id!,
                    stopCount: (r.stops || []).length,
                    defaultDriverUid: r.defaultDriverUid || null,
                    defaultDriverName: r.defaultDriverName || '',
                    generating: false
                };
            });
    }

    // One live subscription per driver on the board. Positions only flow while
    // the driver has the app open — so silence is normal, and the label says
    // "last seen", greying out after 15 minutes rather than pretending to be live.
    private watchDriverLocations() {
        for (const run of this.runs) {
            const uid = run.driverUid;
            if (!uid || this.locationSubs.has(uid)) continue;
            this.locationSubs.set(uid, this.svc.driverLocation$(uid).subscribe(loc => {
                if (!loc || typeof loc.lat !== 'number') return;
                const at = Number(loc.at) || 0;
                this.lastSeen = {
                    ...this.lastSeen,
                    [uid]: {
                        label: 'Last seen ' + moment.unix(at).format('HH:mm'),
                        mapsUrl: `https://maps.google.com/?q=${loc.lat},${loc.lng}`,
                        stale: moment().unix() - at > 15 * 60
                    }
                };
            }));
        }
    }

    // ---- actions -------------------------------------------------------------

    async generate(row: UnscheduledRoute) {
        if (!this.canEdit || row.generating) return;
        row.generating = true;
        try {
            const res: any = await this.svc.generateRun(row.id, this.date, this.genDriver[row.id] || null);
            this.notifier.showNotification('success',
                `Run created for ${row.name} — ${res.stopCount} stops`);
        } catch (err: any) {
            // The server refuses to regenerate a run a driver has started; that
            // message is the useful one.
            const msg = err?.error?.message || err?.message || 'Could not generate the run';
            this.notifier.showNotification('error', msg);
        }
        row.generating = false;
    }

    openInDispatch(run: RunCard) {
        this.router.navigate(['/dashboard/delivery/dispatch'],
            { queryParams: { date: this.date, run: run.id } });
    }

    openDispatch() {
        this.router.navigate(['/dashboard/delivery/dispatch'], { queryParams: { date: this.date } });
    }

    openIssues() {
        this.router.navigate(['/dashboard/delivery/issues']);
    }

    openRoutes() {
        this.router.navigate(['/dashboard/delivery/routes']);
    }

    // ---- shaping -------------------------------------------------------------

    private time(unix: any): string {
        return unix ? moment.unix(Number(unix)).format('HH:mm') : '';
    }

    private toCard(r: any): RunCard {
        const labels: { [key: string]: [string, string, number] } = {
            assigned: ['Not started', 'off', 3],
            picked_up: ['Boxes loaded', 'warn', 1],
            in_progress: ['On the road', 'warn', 0],
            completed: ['Completed', 'ok', 4],
            abandoned: ['Abandoned', 'bad', 2]
        };
        const [statusLabel, statusClass, weight] = labels[r.status] || [r.status || '—', 'off', 3];
        const total = Number(r.stopCount) || 0;
        const done = Number(r.completedStopCount) || 0;

        let whereLabel = '';
        if (r.status === 'in_progress') {
            whereLabel = r.atStopName
                ? `At ${r.atStopName}` + (r.atStopOrder ? ` (stop ${r.atStopOrder})` : '')
                : (r.lastDoneName ? `Driving — last done ${r.lastDoneName}` : 'On the road');
        } else if (r.status === 'picked_up') {
            whereLabel = 'Boxes loaded — not at a stop yet';
        }

        const mins = Number(r.expectedMinutesTotal) || 0;
        const expectedLabel = mins
            ? (mins >= 60
                ? `~${Math.floor(mins / 60)}h ${mins % 60 ? mins % 60 + 'min' : ''} on site`
                : `~${mins} min on site`)
            : '';

        const from = r.takenOverFrom;

        return {
            id: r.id,
            routeId: r.routeId || '',
            routeName: r.routeName || r.routeId || '—',
            driverUid: r.driverUid || null,
            driverName: r.driverName || 'No driver assigned',
            status: r.status,
            statusLabel, statusClass, weight,
            stopCount: total,
            completedStopCount: done,
            skippedStopCount: Number(r.skippedStopCount) || 0,
            progressPct: total ? Math.round(done / total * 100) : 0,
            whereLabel,
            lastActivity: this.time(r.lastActivityAt),
            pickedUpAt: this.time(r.pickedUpAt),
            completedAt: this.time(r.completedAt),
            expectedLabel,
            issueCount: Number(r.issueCount) || 0,
            coveringLabel: from ? `covering for ${from.name || 'another driver'}` : ''
        };
    }

    trackByRun(_: number, r: RunCard) { return r.id; }
    trackByRoute(_: number, r: UnscheduledRoute) { return r.id; }
    trackByIssue(_: number, i: IssueCard) { return i.id; }
}
